import { useEffect, useRef } from 'react'

export const AudioWave = ({
  audioContext,
  visStream,
  className,
}: {
  audioContext: AudioContext | null
  visStream?: MediaStream | null
  className?: string
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  const startVisualization = async () => {
    if (!audioContext || !visStream) {
      return
    }

    const analyser = audioContext.createAnalyser()
    const source = audioContext.createMediaStreamSource(visStream)
    source.connect(analyser)
    analyser.fftSize = 512
    draw(analyser)
  }

  const draw = (analyser: AnalyserNode) => {
    const canvas = canvasRef.current
    if (!canvas) {
      return
    }
    // Next code is required to fix blurry canvas on retina screens.
    // Get the DPR and size of the canvas
    const dpr = window.devicePixelRatio
    const rect = canvas.getBoundingClientRect()

    // Set the "actual" size of the canvas
    canvas.width = rect.width * dpr
    canvas.height = rect.height * dpr

    const ctx = canvas.getContext('2d')
    if (!ctx) {
      return
    }

    // Scale the context to ensure correct drawing operations
    ctx.scale(dpr, dpr)

    // Set the "drawn" size of the canvas
    canvas.style.width = `${rect.width}px`
    canvas.style.height = `${rect.height}px`

    const WIDTH = canvas.width
    const HEIGHT = canvas.height

    // Clear the previous drawing
    ctx.clearRect(0, 0, WIDTH, HEIGHT)

    const bufferLength = analyser.frequencyBinCount
    const dataArray = new Uint8Array(bufferLength)
    const previousHeights = new Float32Array(bufferLength)

    const barWidth = Math.ceil(WIDTH / dataArray.length)

    function drawSmoothBars() {
      if (!ctx) {
        return
      }
      requestAnimationFrame(drawSmoothBars)
      analyser.getByteFrequencyData(dataArray)

      ctx.fillStyle = 'white' // Background color of the canvas
      ctx.fillRect(0, 0, WIDTH, HEIGHT)

      const smoothingFactor = 0.97
      const updateFactor = 1 - smoothingFactor
      const gain = 2.0 // Adjust the gain factor as needed

      ctx.fillStyle = '#d9d9d9' // Bar color
      ctx.beginPath()
      ctx.moveTo(0, HEIGHT) // Start from bottom-left corner

      let x = 0
      for (let i = 0; i < bufferLength && x < WIDTH; i++) {
        // Amplify the audio data
        const amplifiedValue = dataArray[i] * gain

        // Adjust the smoothing
        const barHeight =
          previousHeights[i] * smoothingFactor + amplifiedValue * updateFactor
        previousHeights[i] = barHeight

        const y = Math.ceil(HEIGHT - (barHeight / 256) * HEIGHT)

        ctx.lineTo(x, y) // Draw line to the top of the bar

        x += barWidth
      }

      ctx.lineTo(WIDTH, HEIGHT) // Draw line to bottom-right corner
      ctx.closePath()
      ctx.fill()
    }

    drawSmoothBars()
  }

  useEffect(() => {
    startVisualization().catch(console.error)
  }, [audioContext, visStream])

  return (
    <canvas className={className} ref={canvasRef} width="250" height="40" />
  )
}
